import { useCallback, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { capabilityApi } from '../api';
import { useAsync } from '../components/useAsync';
import PageShell from '../components/PageShell';
import DataTable from '../components/DataTable';
import GrantCapabilityModal from '../components/GrantCapabilityModal';
import type { CapabilityGrant, CapabilityRequest } from '../types';
import { capabilityLabel } from '../types';

/**
 * Everything a user holds beyond their role: the asking, and the answer.
 *
 * This screen replaced a Slack message and a boolean. A customer needing one
 * extra permission asked in Slack and a staff member set a per-user flag —
 * global across every workspace they belonged to, with no record of who granted
 * it or why, and no way to make it temporary. Here the ask, the decision, the
 * reason and the expiry are all recorded, and a grant applies to one workspace.
 *
 * Named for the grants rather than the queue — it was "Access requests" while
 * approving a request was the only way to create one. Granting directly is now
 * a first-class path (staff agreeing to something in a call, where no request
 * exists to approve), so the requests are one tab of the subject rather than
 * the subject itself.
 */
export default function AccessesPage() {
  // Only whether anything is waiting, which is all the dot needs — the tab body
  // fetches the rows it renders. Asked for on the standing tab rather than the
  // queue's own, because an unattended queue is invisible from anywhere else.
  const pending = useAsync(
    () => capabilityApi.requests({ status: 'pending', per_page: 1 }),
    [],
  );
  const waiting = pending.data?.total ?? 0;

  return (
    <PageShell
      title="Accesses"
      subtitle={
        <>
          Capabilities users hold beyond their role, each scoped to one workspace
          &mdash; never the whole account. Requests are the usual way one starts;
          staff can also grant directly.
        </>
      }
    >
      {/* Grants first, and so the tab that opens: it answers the standing
          question — who holds what right now — while Requests is an inbox,
          worth opening when something is in it. The dot is what says so. */}
      <Tabs
        className="panel-tabs"
        items={[
          { key: 'grants', label: 'Grants', children: <GrantsTable /> },
          {
            key: 'requests',
            label: (
              // `dot` renders whether or not there is a count behind it, so it
              // is switched off explicitly rather than left to the count.
              <Badge dot={waiting > 0} offset={[6, -1]}>
                Requests
              </Badge>
            ),
            children: <RequestQueue onReviewed={pending.reload} />,
          },
        ]}
      />
    </PageShell>
  );
}

const STATUS_COLOR: Record<string, string> = {
  pending: 'gold',
  approved: 'green',
  declined: 'red',
  withdrawn: 'default',
};

function RequestQueue({ onReviewed }: { onReviewed: () => void }) {
  const [status, setStatus] = useState<string>('pending');
  const [detail, setDetail] = useState<CapabilityRequest | null>(null);

  const requests = useAsync(
    () =>
      capabilityApi.requests({
        status: status === 'all' ? undefined : status,
        per_page: 100,
      }),
    [status],
  );

  // A decision removes a row from the pending queue, so the tab badge is stale
  // the moment this list is.
  const reload = useCallback(() => {
    requests.reload();
    onReviewed();
  }, [requests, onReviewed]);

  const columns: ColumnsType<CapabilityRequest> = [
    {
      title: 'Requester',
      key: 'user',
      render: (_, row) => (
        <div>
          <Button type="link" style={{ padding: 0 }} onClick={() => setDetail(row)}>
            {row.user_name || row.user_email}
          </Button>
          <div className="mono" style={{ color: '#6b7280' }}>
            {row.user_email}
          </div>
        </div>
      ),
    },
    {
      title: 'Wants',
      key: 'capability',
      render: (_, row) => <Tag color="gold">{capabilityLabel(row.capability)}</Tag>,
    },
    {
      title: 'Where',
      key: 'workspace',
      render: (_, row) => (
        <div>
          {row.org_name}
          <div className="mono" style={{ color: '#6b7280' }}>
            {row.workspace_slug}
          </div>
        </div>
      ),
    },
    {
      title: 'Why',
      key: 'justification',
      render: (_, row) =>
        row.justification ? (
          <Tooltip title={row.justification}>
            <span style={{ display: 'inline-block', maxWidth: 260 }}>
              <Typography.Text ellipsis>{row.justification}</Typography.Text>
            </span>
          </Tooltip>
        ) : (
          <Typography.Text type="secondary">&mdash;</Typography.Text>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, row) => (
        <Tag color={STATUS_COLOR[row.status] ?? 'default'}>{row.status}</Tag>
      ),
    },
    {
      title: 'Asked',
      key: 'created_at',
      width: 120,
      render: (_, row) =>
        row.created_at ? dayjs(row.created_at).format('D MMM YYYY') : '—',
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      render: (_, row) =>
        row.status === 'pending' ? (
          <Space size={4}>
            <ApproveButton request={row} onDone={reload} />
            <DeclineButton request={row} onDone={reload} />
          </Space>
        ) : null,
    },
  ];

  return (
    <>
      <div className="panel-toolbar">
        <Segmented
          value={status}
          onChange={(v) => setStatus(v as string)}
          options={[
            { label: 'Pending', value: 'pending' },
            { label: 'Approved', value: 'approved' },
            { label: 'Declined', value: 'declined' },
            { label: 'All', value: 'all' },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={reload}>
          Refresh
        </Button>
      </div>

      <DataTable<CapabilityRequest>
        minWidth={1120}
        rowKey="id"
        loading={requests.loading}
        columns={columns}
        dataSource={requests.data?.items ?? []}
        pagination={false}
        locale={{
          emptyText: status === 'pending' ? 'Nothing waiting for review.' : 'No requests.',
        }}
      />

      <Drawer
        title="Request detail"
        width={560}
        open={detail !== null}
        onClose={() => setDetail(null)}
        destroyOnClose
      >
        {detail && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Requester">
              {detail.user_name}
              <div className="mono">{detail.user_email}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Capability">
              {capabilityLabel(detail.capability)}
              <div className="mono" style={{ color: '#6b7280' }}>
                {detail.capability}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Organization">{detail.org_name}</Descriptions.Item>
            <Descriptions.Item label="Workspace">
              {detail.workspace_name}
              <div className="mono">{detail.workspace_slug}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Justification">
              {detail.justification || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={STATUS_COLOR[detail.status]}>{detail.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Decision note">
              {detail.decision_note || '—'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </>
  );
}

function ApproveButton({
  request,
  onDone,
}: {
  request: CapabilityRequest;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [expiry, setExpiry] = useState<dayjs.Dayjs | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await capabilityApi.approve(request.id, {
        expires_at: expiry ? expiry.toISOString() : undefined,
        decision_note: note.trim() || undefined,
      });
      message.success('Approved. The grant is effective immediately.');
      setOpen(false);
      onDone();
    } catch {
      /* toast already shown by the axios layer */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Button type="primary" size="small" onClick={() => setOpen(true)}>
        Approve
      </Button>
      <Modal
        title={`Approve: ${capabilityLabel(request.capability)}`}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        okText="Approve"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Grants ${request.user_email} this capability in ${request.workspace_slug} only`}
          description="Other workspaces they belong to are unaffected."
        />
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Typography.Text strong>Expires</Typography.Text>
            <DatePicker
              style={{ width: '100%', marginTop: 6 }}
              value={expiry}
              onChange={setExpiry}
              disabledDate={(d) => d.isBefore(dayjs(), 'day')}
              placeholder="No expiry"
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Leave empty for no expiry. For a one-off need, a date is safer &mdash;
              it&apos;s the thing the old per-user flag could never do.
            </Typography.Text>
          </div>
          <div>
            <Typography.Text strong>Reason</Typography.Text>
            <Input.TextArea
              rows={3}
              style={{ marginTop: 6 }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Recorded on the grant and shown to the requester"
            />
          </div>
        </Space>
      </Modal>
    </>
  );
}

function DeclineButton({
  request,
  onDone,
}: {
  request: CapabilityRequest;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await capabilityApi.decline(request.id, note.trim() || undefined);
      message.success('Request declined.');
      setOpen(false);
      onDone();
    } catch {
      /* toast already shown */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Button size="small" danger onClick={() => setOpen(true)}>
        Decline
      </Button>
      <Modal
        title={`Decline: ${capabilityLabel(request.capability)}`}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        okText="Decline"
        okButtonProps={{ danger: true }}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
          The requester sees this reason, so it&apos;s worth filling in &mdash;
          otherwise they only learn that the answer was no.
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why this is being declined"
        />
      </Modal>
    </>
  );
}

function GrantsTable() {
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [grantOpen, setGrantOpen] = useState(false);

  const grants = useAsync(
    () => capabilityApi.grants({ include_revoked: includeRevoked, per_page: 100 }),
    [includeRevoked],
  );

  const revoke = useCallback(
    async (grant: CapabilityGrant) => {
      setBusy(grant.id);
      try {
        await capabilityApi.revokeGrant(grant.id);
        message.success('Grant revoked.');
        grants.reload();
      } catch {
        /* toast already shown */
      } finally {
        setBusy(null);
      }
    },
    [grants],
  );

  const columns: ColumnsType<CapabilityGrant> = [
    {
      title: 'User',
      key: 'user',
      render: (_, row) => <span className="mono">{row.user_email}</span>,
    },
    {
      title: 'Capability',
      key: 'capability',
      render: (_, row) => <Tag color="gold">{capabilityLabel(row.capability)}</Tag>,
    },
    {
      title: 'Where',
      key: 'workspace',
      render: (_, row) => (
        <div>
          {row.org_name}
          <div className="mono" style={{ color: '#6b7280' }}>
            {row.workspace_slug}
          </div>
        </div>
      ),
    },
    {
      title: 'Expires',
      key: 'expires_at',
      width: 130,
      render: (_, row) =>
        row.expires_at ? (
          dayjs(row.expires_at).format('D MMM YYYY')
        ) : (
          <Typography.Text type="secondary">Never</Typography.Text>
        ),
    },
    {
      title: 'State',
      key: 'state',
      width: 120,
      // is_live comes from the server and accounts for expiry as well as
      // revocation, so an un-revoked row can still be dead.
      render: (_, row) =>
        row.revoked_at ? (
          <Tag color="red">Revoked</Tag>
        ) : row.is_live ? (
          <Tag color="green">Live</Tag>
        ) : (
          <Tag color="orange">Expired</Tag>
        ),
    },
    {
      title: 'Reason',
      key: 'reason',
      render: (_, row) =>
        row.reason ? (
          <Tooltip title={row.reason}>
            <span style={{ display: 'inline-block', maxWidth: 220 }}>
              <Typography.Text ellipsis>{row.reason}</Typography.Text>
            </span>
          </Tooltip>
        ) : (
          <Typography.Text type="secondary">&mdash;</Typography.Text>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, row) =>
        row.revoked_at ? null : (
          <Popconfirm
            title="Revoke this grant?"
            description="Access stops immediately. The record is kept."
            onConfirm={() => revoke(row)}
          >
            <Button size="small" danger type="text" loading={busy === row.id}>
              Revoke
            </Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <>
      <div className="panel-toolbar">
        <Segmented
          value={includeRevoked ? 'all' : 'active'}
          onChange={(v) => setIncludeRevoked(v === 'all')}
          options={[
            { label: 'Active', value: 'active' },
            { label: 'Including revoked', value: 'all' },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={grants.reload}>
          Refresh
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ marginLeft: 'auto' }}
          onClick={() => setGrantOpen(true)}
        >
          Grant capability
        </Button>
      </div>

      <DataTable<CapabilityGrant>
        minWidth={1120}
        rowKey="id"
        loading={grants.loading}
        columns={columns}
        dataSource={grants.data?.items ?? []}
        pagination={false}
        locale={{ emptyText: 'No grants. Everyone is working within their role.' }}
      />

      {/* Mounted only while open, so each grant starts on an empty form. */}
      {grantOpen && (
        <GrantCapabilityModal
          onClose={() => setGrantOpen(false)}
          onGranted={grants.reload}
        />
      )}
    </>
  );
}
